import { ref, watch, onMounted } from 'vue'
import { useOnlineStatus } from './useOnlineStatus'
import { useItemStore } from '@/stores/items'
import { getChanges, addChange, removeChange, getImage, removeImage } from '@/lib/offlineDb'
import {
  createItem as apiCreateItem,
  updateItem as apiUpdateItem,
  deleteItem as apiDeleteItem,
  uploadItemImage,
  getItem,
} from '@/api/items'
import type { PendingChange } from '@/lib/offlineDb'
import type { CreateItemInput, UpdateItemInput } from '@collector/shared'
import type { LocalItemState } from '@/types/offline'

export function useSyncEngine() {
  const { isOnline } = useOnlineStatus()
  const itemStore = useItemStore()

  const isSyncing = ref(false)
  const syncProgress = ref({ current: 0, total: 0 })
  const syncResult = ref<'idle' | 'success' | 'partial-failure'>('idle')
  const failedCount = ref(0)

  let syncTimeout: ReturnType<typeof setTimeout> | null = null
  let successTimeout: ReturnType<typeof setTimeout> | null = null

  watch(isOnline, (online, wasOnline) => {
    if (online && !wasOnline) {
      if (syncTimeout) clearTimeout(syncTimeout)
      syncTimeout = setTimeout(() => processQueue(), 2000)
    }
  })

  async function processQueue(): Promise<void> {
    if (!isOnline.value) return

    const changes = await getChanges()
    const pending = changes.filter((c) => c.status === 'pending')
    if (pending.length === 0) return

    isSyncing.value = true
    syncProgress.value = { current: 0, total: pending.length }
    failedCount.value = 0

    for (const change of pending) {
      try {
        await processChange(change)
        await removeChange(change.id)
        clearPendingFlag(change)
      } catch (err) {
        await markChangeFailed(change, err)
        markItemSyncError(change, err)
        failedCount.value++
      }
      syncProgress.value = {
        current: syncProgress.value.current + 1,
        total: syncProgress.value.total,
      }
    }

    isSyncing.value = false
    syncResult.value = failedCount.value > 0 ? 'partial-failure' : 'success'

    if (syncResult.value === 'success') {
      if (successTimeout) clearTimeout(successTimeout)
      successTimeout = setTimeout(() => {
        syncResult.value = 'idle'
      }, 3000)
    }
  }

  async function processChange(change: PendingChange): Promise<void> {
    switch (change.type) {
      case 'create': {
        const serverItem = await apiCreateItem(change.data as CreateItemInput)
        replaceOptimisticItem(change.tempId!, serverItem)
        const imageData = await getImage(change.tempId!)
        if (imageData) {
          const file = new File([imageData.blob], 'image.jpg', { type: imageData.mimeType })
          await uploadItemImage(serverItem.id, file)
          await removeImage(change.tempId!)
          // Refresh item from server to get the updated image URLs
          const updated = await getItem(serverItem.id)
          const idx = itemStore.items.findIndex((i) => i.id === serverItem.id)
          if (idx !== -1) {
            itemStore.items[idx] = { ...updated } as LocalItemState
          }
        }
        break
      }
      case 'update': {
        const serverItem = await apiUpdateItem(change.entityId!, change.data as UpdateItemInput)
        const localItem = itemStore.items.find((i) => i.id === change.entityId)
        if (localItem && new Date(serverItem.updatedAt) > new Date(localItem.updatedAt)) {
          console.warn(
            `[sync] Conflict detected for item ${change.entityId}: server had newer version. Local changes applied (last-write-wins).`
          )
        }
        break
      }
      case 'delete': {
        try {
          await apiDeleteItem(change.entityId!)
        } catch (err: unknown) {
          const apiErr = err as { response?: { status?: number } }
          if (apiErr.response?.status === 404) {
            // Item already deleted on server — treat as success
            return
          }
          throw err
        }
        break
      }
      case 'upload': {
        const key = change.tempId || String(change.entityId)
        const blobData = await getImage(key)
        if (blobData) {
          const file = new File([blobData.blob], 'image.jpg', { type: blobData.mimeType })
          await uploadItemImage(change.entityId!, file)
          await removeImage(key)
        }
        break
      }
    }
  }

  function replaceOptimisticItem(
    tempId: string,
    serverItem: import('@collector/shared').ItemResponse
  ) {
    const idx = itemStore.items.findIndex((i) => (i as LocalItemState)._tempId === tempId)
    if (idx !== -1) {
      const old = itemStore.items[idx] as LocalItemState
      if (old.imageThumbnail?.startsWith('blob:')) {
        URL.revokeObjectURL(old.imageThumbnail)
      }
      itemStore.items[idx] = { ...serverItem } as LocalItemState
    }
  }

  function clearPendingFlag(change: PendingChange) {
    if (change.type === 'create') return // already replaced via replaceOptimisticItem
    const item = itemStore.items.find((i) => i.id === change.entityId) as LocalItemState | undefined
    if (item) {
      item._pendingSync = false
      item._syncError = undefined
    }
  }

  function markItemSyncError(change: PendingChange, err: unknown) {
    const message = err instanceof Error ? err.message : 'Sync failed'
    let item: LocalItemState | undefined

    if (change.type === 'create') {
      item = itemStore.items.find((i) => (i as LocalItemState)._tempId === change.tempId) as
        | LocalItemState
        | undefined
    } else {
      item = itemStore.items.find((i) => i.id === change.entityId) as LocalItemState | undefined
    }

    if (item) {
      item._syncError = message
    }
  }

  async function markChangeFailed(change: PendingChange, err: unknown): Promise<void> {
    const message = err instanceof Error ? err.message : 'Unknown error'
    await addChange({ ...change, status: 'failed', error: message })
    // Remove the pending version so the failed version replaces it
    // (addChange uses put so it overwrites by id — no extra remove needed)
  }

  async function retryFailed(): Promise<void> {
    // Reset failed changes back to pending
    const changes = await getChanges()
    const failed = changes.filter((c) => c.status === 'failed')
    for (const change of failed) {
      await addChange({ ...change, status: 'pending', error: undefined })
      // Clear error indicator on item
      markItemSyncError(change, new Error(''))
      const item = itemStore.items.find(
        (i) => i.id === change.entityId || (i as LocalItemState)._tempId === change.tempId
      ) as LocalItemState | undefined
      if (item) item._syncError = undefined
    }
    syncResult.value = 'idle'
    await processQueue()
  }

  async function discardFailed(): Promise<void> {
    const changes = await getChanges()
    const failed = changes.filter((c) => c.status === 'failed')

    for (const change of failed) {
      await removeChange(change.id)

      if (change.type === 'create' && change.tempId) {
        // Remove optimistic item from store
        const idx = itemStore.items.findIndex(
          (i) => (i as LocalItemState)._tempId === change.tempId
        )
        if (idx !== -1) {
          const item = itemStore.items[idx] as LocalItemState
          if (item.imageThumbnail?.startsWith('blob:')) {
            URL.revokeObjectURL(item.imageThumbnail)
          }
          itemStore.items.splice(idx, 1)
        }
        await removeImage(change.tempId)
      } else if (change.type === 'upload' && change.tempId) {
        await removeImage(change.tempId)
        // Revert thumbnail on the item
        const item = itemStore.items.find((i) => i.id === change.entityId) as
          | LocalItemState
          | undefined
        if (item) {
          item._pendingSync = false
          item._syncError = undefined
          item.imageThumbnail = null
        }
      } else {
        // For failed updates/deletes: just clear the error flag
        const item = itemStore.items.find((i) => i.id === change.entityId) as
          | LocalItemState
          | undefined
        if (item) {
          item._pendingSync = false
          item._syncError = undefined
        }
      }
    }

    syncResult.value = 'idle'
    failedCount.value = 0
  }

  async function restoreOptimisticItems(
    changes: import('@/lib/offlineDb').PendingChange[]
  ): Promise<void> {
    for (const change of changes) {
      if (change.type === 'create' && change.tempId) {
        // Only add if not already in store
        const existing = itemStore.items.find(
          (i) => (i as LocalItemState)._tempId === change.tempId
        )
        if (existing) continue

        const imageData = await getImage(change.tempId)
        const thumbnailUrl = imageData ? URL.createObjectURL(imageData.blob) : null
        const data = change.data as CreateItemInput & Record<string, unknown>
        const now = new Date().toISOString()
        itemStore.items.unshift({
          id: -Date.now(),
          userId: 1,
          name: data.name,
          description: data.description ?? null,
          category: data.category ?? 'other',
          isVirtual: data.isVirtual ?? false,
          artistOrManufacturer: data.artistOrManufacturer ?? null,
          origin: data.origin ?? null,
          location: data.location ?? null,
          notes: data.notes ?? null,
          dateAcquired: data.dateAcquired ?? null,
          acquiredFrom: data.acquiredFrom ?? null,
          museumOrGallery: data.museumOrGallery ?? null,
          city: data.city ?? null,
          tags: data.tags ?? [],
          imageThumbnail: thumbnailUrl,
          imageLarge: null,
          createdAt: now,
          updatedAt: now,
          _pendingSync: true,
          _tempId: change.tempId,
        } as LocalItemState)
      }
    }
  }

  onMounted(async () => {
    const changes = await getChanges()
    if (changes.length === 0) return

    if (isOnline.value) {
      await processQueue()
    } else {
      await restoreOptimisticItems(changes)
    }
  })

  return {
    isSyncing,
    syncProgress,
    syncResult,
    failedCount,
    processQueue,
    retryFailed,
    discardFailed,
  }
}
